import { defineStore } from "pinia";
import { axiosClient } from "@/helpers/auth";
import { ref, computed } from "vue";
import { useGlobalStore } from "@/store/storeGlobal.js";
import { useNotificationsStore } from "@/store/notificationsStore";

const debugPrefix = '[Verdiliste]';

export const useVerdilisteStore = defineStore("verdilisteStore", () =>
{

  const globalStore = useGlobalStore();
  const notificationsStore = useNotificationsStore();

  // VARIABLER
  const verdiliste_liste = ref(null);           // Alle verdilister
  const verdiliste_object_liste = ref(null);    // Alle objekter av type tabell/prosedyre i valgt skjema
  const verdiliste_kolonne_liste = ref(null);   // Alle kolonnene til valgt objekt

  const verdilisteOptions = computed(() =>
  {
    if (!Array.isArray(verdiliste_liste.value)) return []
    return verdiliste_liste.value.map(v => ({
      value: v?.valueListId ?? v?.id ?? v?.valueListID ?? null,
      label: v?.description ?? v?.name ?? v?.objectName ?? v?.objektNavn ?? ''
    })).filter(o => o.value != null && String(o.label).length > 0)
  })

  // Cached verdlister
  const verdiLister = ref({})
  let loadingVerdiLister = []
  const pendingPromises = ref({})

  function isLoading (valueListId)
  {
    return loadingVerdiLister.includes(valueListId);
  }

  function isLoaded (valueListId)
  {
    if (isLoading(valueListId))
      return false;
    return !!verdiLister.value[valueListId];
  }

  function getVerdiListe (valueListId)
  {
    if (isLoaded(valueListId)) {
      return verdiLister.value[valueListId]?.map(item => ({
        value: item.key,
        label: item.value
      }));
    }
    if (isLoading(valueListId)) {
      console.warn('Laster allerede verdiliste: ', valueListId)
      return false;
    }
    console.error('Verdiliste ikke lastet: ', valueListId)
    return false;
  }

  async function getVerdiListeAsync (valueListId)
  {
    if (!valueListId) return []
    if (isLoaded(valueListId)) return getVerdiListe(valueListId)
    // Hvis det allerede pågår et kall, vent på det
    if (pendingPromises.value[valueListId]) {
      await pendingPromises.value[valueListId]
      return getVerdiListe(valueListId) || []
    }
    // Start fetch og cache promise
    pendingPromises.value[valueListId] = (async () =>
    {
      await fetchVerdiListe(valueListId)
    })()
    try {
      await pendingPromises.value[valueListId]
    } finally {
      delete pendingPromises.value[valueListId]
    }
    return getVerdiListe(valueListId) || []
  }

  async function fetchVerdiListe (valueListId)
  {
    globalStore.debug(`${debugPrefix} fetchVerdiListe`, valueListId);
    if (isLoading(valueListId)) {
      console.warn('Laster allerede verdiliste: ', valueListId)
      return;
    }
    if (isLoaded(valueListId)) {
      globalStore.debug(`${debugPrefix} Allerede lastet: `, verdiLister.value[valueListId]);
      return;
    }
    loadingVerdiLister[valueListId] = true;

    const params = { valueListId };
    const respons = await axiosClient.get('v2/value-list', { params });
    verdiLister.value[valueListId] = respons?.data;
    loadingVerdiLister[valueListId] = false;
  }

  function verdiliste_Init ()
  {
    // Nullstiller alle verdier
    verdiliste_object_liste.value = null;
    verdiliste_kolonne_liste.value = null;
  }

  // verdiliste_GetAll
  async function verdiliste_GetAll ()
  {
    const respons = await axiosClient.get('v2/value-lists');
    verdiliste_liste.value = respons?.data;
    globalStore.debug(`${debugPrefix} verdiliste_liste`, verdiliste_liste.value);
  }

  async function deleteValueList (verdilisteId)
  {
    const params = {
      valueListId: verdilisteId
    };
    await axiosClient.delete('v2/value-list', { params });
  }

  // verdiliste_Save
  async function verdiliste_Save (valueList)
  {
    const response = await axiosClient.post('v2/value-list', valueList);
    verdiliste_Init()
    await verdiliste_GetAll();
    if (response) {
      notificationsStore.addSuccess("Verdiliste er lagret");
    }
  }

  // schema_GetObjects
  async function schema_getObjects (dbSchemaName)
  {
    const params = {
      schemaName: dbSchemaName
    };
    const respons = await axiosClient.get('v2/database/system/objects', { params });
    verdiliste_object_liste.value = respons?.data;
  }

  // schema-object-columns-get (nøkkelkolonne, verdikolonner)
  async function schema_ObjectColumns_Get (dbSchemaName, dbObjektNavn)
  {
    const params = {
      schemaName: dbSchemaName,
      objectName: dbObjektNavn
    };
    const respons = await axiosClient.get('v2/database/system/object', { params });
    verdiliste_kolonne_liste.value = respons?.data;
  }

  return {
    getVerdiListe, getVerdiListeAsync, fetchVerdiListe, verdiLister, isLoading,
    verdiliste_liste, verdiliste_Init, verdiliste_GetAll, verdiliste_Save, deleteValueList,
    schema_getObjects, verdiliste_object_liste,
    schema_ObjectColumns_Get, verdiliste_kolonne_liste, verdilisteOptions
  }
})
